import { UnreachableCaseError } from '@math-blocks/core';

import * as Layout from './layout';
import * as types from './types';

import type { FontData } from '@math-blocks/opentype';

type Style = {
  readonly fill?: string;
  readonly stroke?: string;
};

type Common = {
  readonly id?: number;
  readonly style: Style;
  readonly key?: string;
  readonly className?: string;
};

export type Group = {
  readonly type: 'group';
  // pen position of the group within its parent
  readonly x: number;
  readonly y: number;
  // `bounds` includes height and depth which is even information to compute
  // the bounding box of the group in `findIntersections`.
  readonly bounds: types.Dim;
  readonly children: readonly Node[];
} & Common;

export type Glyph = {
  readonly type: 'char';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly glyph: types.Glyph;
} & Common;

export type InterpolatedGlyph = {
  readonly type: 'interpolated';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly interpolatedGlyph: types.InterpolatedGlyph;
  readonly amount: number;
} & Common;

export type Line = {
  readonly type: 'line';
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly thickness: number;
} & Common;

export type Rect = {
  readonly type: 'rect';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly fill?: string;
  readonly stroke?: string;
  readonly flag?: 'start' | 'end';
} & Common;

export type Node = Group | Glyph | InterpolatedGlyph | Line | Rect;

export type Point = {
  readonly x: number;
  readonly y: number;
};

const processHRule = (hrule: types.HRule, loc: Point): Node => {
  const advance = Layout.getWidth(hrule);
  return {
    type: 'line',
    x1: loc.x,
    y1: loc.y,
    x2: loc.x + advance,
    y2: loc.y,
    thickness: hrule.thickness,
    style: {
      stroke: hrule.style.color,
    },
    id: hrule.id,
  };
};

const processGlyph = (glyph: types.Glyph, loc: Point): Node => {
  return {
    type: 'char',
    x: loc.x,
    y: loc.y,
    width: Layout.getWidth(glyph),
    glyph: glyph,
    style: {
      fill: glyph.style.color,
    },
    id: glyph.id,
  };
};

const processInterpolatedGlyph = (
  interpolatedGlyph: types.InterpolatedGlyph,
  loc: Point,
): Node => {
  return {
    type: 'interpolated',
    x: loc.x,
    y: loc.y,
    width: Layout.getWidth(interpolatedGlyph),
    interpolatedGlyph,
    amount: interpolatedGlyph.amount,
    style: {
      fill: interpolatedGlyph.style.color,
    },
    id: interpolatedGlyph.id,
  };
};

export type LayoutCursor = {
  readonly parent: number;
  readonly prev: number;
  readonly next: number;
  readonly selection: boolean;
};

const CURSOR_WIDTH = 1;

const processHBox = (box: types.HBox, loc: Point, context: Context): Group => {
  const pen = { x: 0, y: 0 };

  const selectionBoxes: Rect[] = [];

  const children: Node[] = [];

  const hasSelection = !context.inSelection && box.content.type === 'selection';

  const {
    layer,
    fontData: { font },
  } = context;
  const { fontSize } = box;
  const parenMetrics = font.getGlyphMetrics(font.getGlyphID(')'));
  // This assumes that parenMetrics.height < font.head.unitsPerEm
  const overshoot = (font.head.unitsPerEm - parenMetrics.height) / 2;

  const ascent =
    ((parenMetrics.bearingY + overshoot) * fontSize) / font.head.unitsPerEm;

  const debugStyle = {
    fill: 'none',
    stroke: 'red',
  };

  // Each cancel region has its own ID. This allows us to have two cancel
  // regions side-by-side without them being merged into one.
  type CancelRegion = {
    id: number;
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };

  let cancelRegion: CancelRegion | null = null;

  const sections = [];
  if (box.content.type === 'static') {
    sections.push(box.content.nodes);
  } else if (box.content.type === 'cursor') {
    sections.push(box.content.left);
    sections.push(box.content.right);
  } else {
    sections.push(box.content.left);
    sections.push(box.content.selection);
    sections.push(box.content.right);
  }

  sections.forEach((section, index) => {
    const isSelection = hasSelection && index === 1;

    // There should only be two sections max.  If there are two sections
    // then we should draw a cursor in between the two of them.
    if (
      index === 1 &&
      !hasSelection &&
      !context.inSelection &&
      context.showCursor &&
      layer === 'selection'
    ) {
      // Draw the cursor.
      children.push({
        type: 'rect',
        x: pen.x - CURSOR_WIDTH / 2,
        y: pen.y - ascent,
        width: CURSOR_WIDTH,
        height: fontSize,
        style: {},
        className: 'blink',
        // We give the cursor rect a unique key whenever we re-render
        // the scene-graph so that it's always visible when moving the
        // cursor.
        key: Math.random().toString(),
      });
    }

    section.forEach((node) => {
      // We've encountered a new cancel id or no cancel id.  Finalize the
      // current cancel region and reset for the next one.
      if (
        cancelRegion &&
        cancelRegion.id !== node.style.cancel &&
        layer === 'content'
      ) {
        children.push({
          type: 'line',
          x1: cancelRegion.xMin,
          y1: cancelRegion.yMax,
          x2: cancelRegion.xMax,
          y2: cancelRegion.yMin,
          thickness: 5,
          style: {},
        });
        cancelRegion = null;
      }
      // If the node has a cancel id, continue the current cancel region
      // or start a new one if there isn't one.
      if (typeof node.style.cancel === 'number' && layer == 'content') {
        const yMin = -Math.max(Layout.getHeight(node), ascent);
        const height = Math.max(
          Layout.getHeight(node) + Layout.getDepth(node),
          fontSize,
        );
        const yMax = yMin + height;

        if (!cancelRegion) {
          cancelRegion = {
            id: node.style.cancel,
            xMin: pen.x,
            xMax: pen.x,
            yMin,
            yMax,
          };
        } else {
          cancelRegion.yMin = Math.min(cancelRegion.yMin, yMin);
          cancelRegion.yMax = Math.max(cancelRegion.yMax, yMax);
        }
      }

      if (isSelection && layer === 'selection') {
        const yMin = -Math.max(Layout.getHeight(node), ascent);

        const height = Math.max(
          Layout.getHeight(node) + Layout.getDepth(node),
          fontSize,
        );

        // Draw the cursor.
        selectionBoxes.push({
          type: 'rect',
          x: pen.x,
          y: yMin,
          width: Layout.getWidth(node),
          height: height,
          style: {},
        });
      }

      const advance = Layout.getWidth(node);
      const height = Layout.getHeight(node);
      const depth = Layout.getDepth(node);

      switch (node.type) {
        case 'HBox':
        case 'VBox': {
          const child = _processBox(
            node,
            { x: pen.x, y: pen.y + node.shift },
            {
              ...context,
              inSelection: context.inSelection || hasSelection,
            },
          );

          // We always have to include child groups regardless of the
          // layer.  TODO: drop this once we flatten the scene graph.
          children.push(child);

          if (layer === 'debug') {
            children.push({
              type: 'rect',
              id: node.id,
              x: pen.x,
              y: pen.y - height,
              width: advance,
              height: depth + height,
              style: debugStyle,
            });
          }

          break;
        }
        case 'HRule': {
          const child = processHRule(node, pen);
          if (layer === 'content') {
            children.push(child);
          }
          break;
        }
        case 'Glyph': {
          const child = processGlyph(node, pen);

          if (layer === 'content') {
            children.push(child);
          }

          if (layer === 'debug') {
            children.push({
              type: 'rect',
              id: node.id,
              x: pen.x,
              y: pen.y - height,
              width: advance,
              height: depth + height,
              style: debugStyle,
            });
          }

          if (layer === 'hitboxes') {
            // TODO: do a second pass on the hitboxes to expand them
            // to their full height
            children.push({
              type: 'rect',
              id: node.id,
              x: pen.x,
              y: pen.y - height,
              width: advance,
              height: depth + height,
              style: debugStyle,
            });
          }

          break;
        }
        case 'InterpolatedGlyph': {
          const child = processInterpolatedGlyph(node, pen);

          if (layer === 'content') {
            children.push(child);
          }

          if (layer === 'debug') {
            children.push({
              type: 'rect',
              id: node.id,
              x: pen.x,
              y: pen.y - height,
              width: advance,
              height: depth + height,
              style: debugStyle,
            });
          }

          if (layer === 'hitboxes') {
            // TODO: do a second pass on the hitboxes to expand them
            // to their full height
            children.push({
              type: 'rect',
              id: node.id,
              x: pen.x,
              y: pen.y - height,
              width: advance,
              height: depth + height,
              style: debugStyle,
            });
          }

          break;
        }
        case 'Kern':
          if (node.flag) {
            if (layer === 'hitboxes') {
              // TODO: do a second pass on the hitboxes to expand
              // them to their full height
              children.push({
                type: 'rect',
                flag: node.flag,
                x: pen.x,
                y: pen.y - box.height,
                width: node.size,
                height: box.depth + box.height,
                style: debugStyle,
              });
            }
          }

          // We don't need to include kerns in the output since we include
          // the cursor or select rectangle in the scene graph.
          break;
        default:
          throw new UnreachableCaseError(node);
      }

      pen.x += advance;

      if (node.style.cancel && cancelRegion) {
        cancelRegion.xMax = pen.x;
      }
    });
  });

  if (cancelRegion !== null && layer === 'content') {
    const thickness = fontSize * font.math.constants.fractionRuleThickness;
    children.push({
      type: 'line',
      // @ts-expect-error: TypeScript doesn't understand forEach loops
      x1: cancelRegion.xMin,
      // @ts-expect-error: TypeScript doesn't understand forEach loops
      y1: cancelRegion.yMax,
      // @ts-expect-error: TypeScript doesn't understand forEach loops
      x2: cancelRegion.xMax,
      // @ts-expect-error: TypeScript doesn't understand forEach loops
      y2: cancelRegion.yMin,
      thickness: thickness,
      style: {},
    });
    cancelRegion = null;
  }

  // Draw the selection.
  if (layer === 'selection') {
    for (const selectionBox of selectionBoxes) {
      children.unshift({
        ...selectionBox,
        fill: 'Highlight',
        stroke: 'none',
      });
    }
  }

  return {
    type: 'group',
    x: loc.x,
    y: loc.y,
    bounds: box,
    children: children,
    style:
      layer === 'content'
        ? {
            fill: box.style.color,
            stroke: box.style.color,
          }
        : {},
    id: box.id,
  };
};

const processVBox = (box: types.VBox, loc: Point, context: Context): Group => {
  const pen = { x: 0, y: 0 };

  pen.y -= box.height;

  const children: Node[] = [];
  const { layer } = context;

  const debugStyle = {
    fill: 'none',
    stroke: 'red',
  };

  box.content.forEach((node) => {
    const width = Layout.getWidth(node);
    const height = Layout.getHeight(node);
    const depth = Layout.getDepth(node);

    switch (node.type) {
      case 'HBox':
      case 'VBox': {
        // TODO: reconsider whether we should be taking the shift into
        // account when computing the height, maybe we can drop this
        // and simplify things.  The reason why we zero out the shift
        // here is that when we render a box inside of a vbox, the shift
        // is a horizontal shift as opposed to a vertical one.
        // I'm not sure we can do this properly since how the shift is
        // used depends on the parent box type.  We could pass that info
        // to the getHeight() function... we should probably do an audit
        // of all the callsites for getHeight()
        const height = Layout.getHeight({ ...node, shift: 0 });
        const depth = Layout.getDepth({ ...node, shift: 0 });

        pen.y += height;
        // TODO: see if we can get rid of this check in the future
        if (Number.isNaN(pen.y)) {
          // eslint-disable-next-line no-debugger
          debugger;
        }

        const child = _processBox(
          node,
          { x: pen.x + node.shift, y: pen.y },
          context,
        );

        // We always have to include child groups regardless of the
        // layer.  TODO: drop this once we flatten the scene graph.
        children.push(child);

        if (layer === 'debug') {
          children.push({
            type: 'rect',
            id: node.id,
            x: pen.x + node.shift,
            y: pen.y - height,
            width: width,
            height: depth + height,
            style: debugStyle,
          });
        }

        pen.y += depth;
        break;
      }
      case 'HRule': {
        pen.y += height;
        const child = processHRule(node, pen);
        if (layer === 'content') {
          children.push(child);
        }
        pen.y += depth;
        break;
      }
      case 'Glyph': {
        // Although there currently isn't anything that uses a glyph
        // in a vbox, we'll likely need it for accents.
        pen.y += height;
        const child = processGlyph(node, pen);

        if (layer === 'content') {
          children.push(child);
        }

        if (layer === 'debug') {
          children.push({
            type: 'rect',
            id: node.id,
            x: pen.x,
            y: pen.y,
            width: width,
            height: depth + height,
            style: debugStyle,
          });
        }

        if (layer === 'hitboxes') {
          // TODO: do a second pass on the hitboxes to expand them
          // to their full height
          children.push({
            type: 'rect',
            id: node.id,
            x: pen.x,
            y: pen.y,
            width: width,
            height: depth + height,
            style: debugStyle,
          });
        }

        pen.y += depth;
        break;
      }
      case 'InterpolatedGlyph': {
        // Although there currently isn't anything that uses a glyph
        // in a vbox, we'll likely need it for accents.
        pen.y += height;
        const child = processInterpolatedGlyph(node, pen);

        if (layer === 'content') {
          children.push(child);
        }

        if (layer === 'debug') {
          children.push({
            type: 'rect',
            id: node.id,
            x: pen.x,
            y: pen.y,
            width: width,
            height: depth + height,
            style: debugStyle,
          });
        }

        if (layer === 'hitboxes') {
          // TODO: do a second pass on the hitboxes to expand them
          // to their full height
          children.push({
            type: 'rect',
            id: node.id,
            x: pen.x,
            y: pen.y,
            width: width,
            height: depth + height,
            style: debugStyle,
          });
        }

        pen.y += depth;
        break;
      }
      case 'Kern':
        pen.y += node.size;
        break;
      default:
        throw new UnreachableCaseError(node);
    }
  });

  return {
    type: 'group',
    x: loc.x,
    y: loc.y,
    bounds: box,
    children: children,
    style:
      layer === 'content'
        ? {
            fill: box.style.color,
            stroke: box.style.color,
          }
        : {},
    id: box.id,
  };
};

type Options = {
  readonly showCursor?: boolean;
  readonly debug?: boolean;
};

type Context = {
  readonly fontData: FontData;
  readonly showCursor: boolean;
  readonly inSelection: boolean;
  // When computing "hitboxes", only Groups and Rect and returned.  Groups
  // remain unchanged from other layers, but Rects and the bounding boxes of
  // Glyphs and horizontal Kerns.
  readonly layer: 'content' | 'selection' | 'debug' | 'hitboxes';
};

const _processBox = (
  box: types.HBox | types.VBox,
  loc: Point,
  context: Context,
): Group => {
  switch (box.type) {
    case 'HBox':
      return processHBox(box, loc, context);
    case 'VBox':
      return processVBox(box, loc, context);
  }
};

export type Scene = {
  readonly width: number;
  readonly height: number;
  // group these into .layers?
  readonly content: Group;
  readonly selection: Group;
  readonly hitboxes: Group;
  readonly debug: Group;
};

export const processBox = (
  box: types.HBox,
  fontData: FontData,
  options: Options = {},
): Scene => {
  const loc = { x: 0, y: Layout.getHeight(box) };
  const context: Context = {
    showCursor: !!options.showCursor,
    inSelection: false,
    fontData: fontData,
    layer: 'content',
  };
  const contentLayer = _processBox(box, loc, context);
  const selectionLayer = _processBox(box, loc, {
    ...context,
    layer: 'selection',
  });
  const debugLayer = _processBox(box, loc, { ...context, layer: 'debug' });
  const hitboxes = _processBox(box, loc, { ...context, layer: 'hitboxes' });

  const scene: Scene = {
    width: Math.ceil(contentLayer.bounds.width),
    height: Math.ceil(contentLayer.bounds.height + contentLayer.bounds.depth),
    content: contentLayer,
    selection: selectionLayer,
    debug: debugLayer,
    hitboxes: hitboxes,
  };

  return scene;
};

type Bounds = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

const isPointInBounds = (
  point: Point,
  bounds: Bounds,
): types.Side | undefined => {
  if (
    point.x > bounds.x &&
    point.x < bounds.x + bounds.width / 2 &&
    point.y > bounds.y &&
    point.y < bounds.y + bounds.height
  ) {
    return 'left';
  }
  if (
    point.x > bounds.x + bounds.width / 2 &&
    point.x < bounds.x + bounds.width &&
    point.y > bounds.y &&
    point.y < bounds.y + bounds.height
  ) {
    return 'right';
  }
  return undefined;
};

const getBounds = (child: Group | Rect, translation: Point): Bounds => {
  return child.type === 'group'
    ? {
        x: child.x + translation.x,
        y: child.y + translation.y - child.bounds.height,
        width: child.bounds.width,
        height: child.bounds.height + child.bounds.depth,
      }
    : {
        x: child.x + translation.x,
        y: child.y + translation.y,
        width: child.width,
        height: child.height,
      };
};

const getIntersection = (
  child: Group | Rect,
  side: types.Side,
): types.Intersection | void => {
  if (child.id) {
    return {
      type: 'content',
      id: child.id,
      side: side,
    };
  } else if (child.type === 'rect' && child.flag) {
    return {
      type: 'padding',
      flag: child.flag,
    };
  }
};

export const findIntersections = (
  point: Point,
  node: Group, // must be the group containing the debug bounding rectangles
  translation?: Point,
): types.Intersection[] => {
  const result: types.Intersection[] = [];

  translation = translation || {
    x: node.x,
    y: node.y,
  };

  for (const child of node.children) {
    if (child.type !== 'group' && child.type !== 'rect') {
      throw new Error('Unexpected node type in hitboxes');
    }

    const bounds = getBounds(child, translation);
    const side = isPointInBounds(point, bounds);

    if (side) {
      const intersection = getIntersection(child, side);
      if (intersection) {
        result.push(intersection);
      }

      if (child.type === 'group') {
        const newTranslation = {
          x: child.x + translation.x,
          y: child.y + translation.y,
        };

        result.push(...findIntersections(point, child, newTranslation));
      }
    }
  }

  if (result.length === 0) {
    // iterate through all of the children again and find any bounds that
    // intersect with the vertical line crossing with `point` on it

    const candidateIntersections: (Group | Rect)[] = [];

    for (const child of node.children) {
      if (child.type !== 'group' && child.type !== 'rect') {
        throw new Error('Unexpected node type in hitboxes');
      }

      const bounds = getBounds(child, translation);

      // We decide which side of the child to put the cursor on once we
      // decide on which candidate is the closest vertically to the point.
      if (point.x > bounds.x && point.x < bounds.x + bounds.width) {
        candidateIntersections.push(child);
      }
    }

    // If there are no candidates don't bother finding the closest one.
    if (candidateIntersections.length === 0) {
      return result;
    }

    const [int, ...rest] = candidateIntersections;

    // The closest candidate is the one with the smallest vertical distance
    // from the point.
    let bounds = getBounds(int, translation);
    let minDist = Math.min(
      Math.abs(bounds.y - point.y),
      Math.abs(bounds.y + bounds.height - point.y),
    );
    let closest = int;

    for (const candidate of rest) {
      bounds = getBounds(candidate, translation);
      const dist = Math.min(
        Math.abs(bounds.y - point.y),
        Math.abs(bounds.y + bounds.height - point.y),
      );
      if (dist < minDist) {
        minDist = dist;
        closest = candidate;
      }
    }

    // Determine whether the cursor should be to the left or the right of
    // the closest node.
    bounds = getBounds(closest, translation);
    const side: types.Side =
      point.x < bounds.x + bounds.width / 2 ? 'left' : 'right';

    if (side) {
      const intersection = getIntersection(closest, side);
      if (intersection) {
        result.push(intersection);
      }

      if (closest.type === 'group') {
        const newTranslation = {
          x: closest.x + translation.x,
          y: closest.y + translation.y,
        };

        result.push(...findIntersections(point, closest, newTranslation));
      }
    }
  }

  return result;
};
